/* eslint-disable no-nested-ternary */
import React from 'react';
import { get, map, forEach, head, fromPairs, find, cloneDeep, findIndex, set, noop, isEmpty } from 'lodash';

// local dependencies
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { DrugPricingDrugDetailService } from 'services/drugPricingDrugDetail';
import { drugPricingDrugDetail as DrugPricingDrugDetailView } from 'ui/views/widgets/drugPricingDrugDetail';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { DrugPricingDrugDetail } from 'ui/containers/DrugPricingDrugDetail';
 *
 * // render
 * <DrugPricingDrugDetail propName='propValue'/>
 */
export class DrugPricingDrugDetail extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            filters: [],
        };

        // create service instance
        this.drugPricingDrugDetailService = new DrugPricingDrugDetailService();
        
        // start data fetch
        this.fetchComponentData();

        // bind
        this.__bind();

    }

    /**
     * @desc bind events and methods
     */
    __bind() {
        log.debug( 'DrugPricingDrugDetail.__bind()' );

        this.handleFilterChange = this.handleFilterChange.bind( this );
        this.handleViewPrice = this.handleViewPrice.bind( this );
    }

    /*********************************************/

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'DrugPricingDrugDetail.fetchComponentData()' );

        // cancel ongoing request
        this.drugPricingDrugDetailService.cancel();

        // fetch component data
        const { status, data } = await this.drugPricingDrugDetailService.fetchComponentData( {
            url: this.props.widget.url,
            data: this.props.requestPayload,
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {

            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
                filters: this.getInitialFiltersState( {
                    filterValues: get( data, 'filterValues', {} ),
                    filters: get( data, 'filters', [] ),
                    options: get( data, 'options', [] ),
                } ),
            } );
        }
    }

    /**
     * @desc Get initial filters state.
     */
    getInitialFiltersState( { filterValues, filters, options } = {} ) {
        log.debug( 'DrugPricingDrugDetail.getInitialFiltersState()' );

        // current options of iteration
        let currentOptions = cloneDeep( options );

        return map( filters, ( filter ) => {
            
            // get selected option (from the `filterValues` of the component data or the first option)
            const filterValue = get( filterValues, filter.name );
            const selectedOption = cloneDeep( isEmpty( filterValue ) ? head( currentOptions ) : find( currentOptions, { value: filterValue } ) );

            // new filter state
            const filterState = {
                ...filter,
                options: currentOptions,
                value: get( selectedOption, 'value', null ),
                disabled: false,
            };

            // update current options
            currentOptions = get( selectedOption, 'options', [] );

            // return new filter state
            return filterState;
        } );
    }
    
    /**
     * @desc Update filters when a filter value changes.
     */
    handleFilterChange( filterName, selectedValue ) {
        log.debug( 'DrugPricingDrugDetail.onFilterChange()' );

        // clone filters from the state
        const filters = cloneDeep( this.state.filters );

        // get index of the current filter in the `filters` list
        const filterIndex = findIndex( filters, { name: filterName } );
        const currentFilter = get( filters, `[${ filterIndex }]`, {} );

        // update current value of filter
        set( currentFilter, 'value', selectedValue );

        // update filters state
        forEach( filters, ( filter, index ) => {
            if( index === filterIndex + 1 ) {
                set( filter, 'value', null ); // update value of the adjacent filter
                set( filter, 'disabled', false ); // enable adjacent filter
            } else if( index > filterIndex + 1 ) {
                set( filter, 'disabled', true ); // disable next filters
                set( filter, 'value', null ); // update value of the next filters
                set( filter, 'options', [] ); // update options of the next filters
            }
        } );

        // get selected option from the `options` of the current filters
        const selectedOption = find( currentFilter.options, { value: selectedValue } );

        // update options of the next filter
        if( filterIndex !== ( this.state.filters.length - 1 ) ) {
            set( filters, `[${ filterIndex + 1 }].options`, get( selectedOption, 'options', [] ) );
        }

        // update filters in the state
        this.setState( {
            filters
        }, () => {
            this.props.onDrugFilterChange(); // execute change callback
        } );
    }
    
    /**
     * @desc When view price button is clicked, execute the callback with filter values.
     */
    handleViewPrice() {
        log.debug( 'DrugPricingDrugDetail.handleViewPrice()' );

        // get quantity object
        const quantityObject = find( this.state.filters, { name: 'quantity' }, {} );
        
        // get the value of quantity
        const quantityValue = get( quantityObject, 'value', '' );

        // get quantity option related to above value
        const quantityOption = find( quantityObject.options, { name: quantityValue } );

        // get drugNdc related to above option
        const drugNdc = get( quantityOption, 'drugNdc', '' );

        // set drugNdc in componentData
        const _componentData = cloneDeep( this.state.componentData );
        _componentData.drugNdc = drugNdc;

        // update state with new componentData
        this.setState( {
            componentData: _componentData
        }, () => {
            this.props.onViewPrice( {
                drugNdc: get( this.state, 'componentData.drugNdc', '' ),
                drugFilters: fromPairs( map( this.state.filters, filter => {
                    return [ filter.name, filter.value ];
                } ) ),
            } );

        } );
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.drugPricingDrugDetailService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <DrugPricingDrugDetailView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                { ...get( this.state, 'componentData', {} ) }
                filters={ this.state.filters }
                onFilterChange={ this.handleFilterChange }
                onViewPrice={ this.handleViewPrice }
            />
        );
    }
}

// set display name
DrugPricingDrugDetail.displayName = 'DrugPricingDrugDetail';

// set default props
DrugPricingDrugDetail.defaultProps = {
    requestPayload: {},
    onDrugFilterChange: noop,
    onViewPrice: noop,
};

// prop types
DrugPricingDrugDetail.propTypes = {};
